import { useState } from 'react';
import { Button } from '../../../UI';
import { WidgetCardPropsModel } from './types';
import { useTranslation } from 'react-i18next';
import { truncateString } from '../../../../helpers/functions';
import { Loading } from '../../../UI';

const WidgetCard = ({
  category,
  className = '',
  description,
  iconUrl,
  isAvailable,
  isInstallButtonLoading,
  isInstalled,
  isLocked,
  isRemoveButtonLoading,
  membershipPlan,
  onInstallClick,
  onPreviewClick,
  onRemoveClick,
  onUpgradeClick,
  suggestedSize = 'COL1H1',
  title,
  viewMode,
}: WidgetCardPropsModel) => {
  const [selectedSize, setSelectedSize] = useState(suggestedSize);
  const { t } = useTranslation();

  const handlePreviewClick = () => {
    if (onPreviewClick) {
      onPreviewClick();
    }
  };

  const handleInstallClick = () => {
    if (onInstallClick) {
      onInstallClick(selectedSize);
    }
  };

  const handleRemoveClick = () => {
    if (onRemoveClick) {
      onRemoveClick();
    }
  };

  const handleUpgradeClick = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
    }
  };

  return viewMode === 'full' ? (
    <div
      className={`flex flex-col justify-between min-w-64 h-60 bg-white border border-grey-10 rounded-4xl shadow-md py-3
        dark:bg-grey-90
        dark:border-grey-80
        ${className}
      `}
    >
      <div className="flex w-full px-3">
        <div
          className={
            'flex bg-lime min-w-16 w-16 h-16 rounded-xl overflow-hidden mr-4 '
          }
        >
          {iconUrl && <img src={iconUrl} alt={title} />}
        </div>
        <div className="flex flex-col justify-start">
          <div className="text-sm font-semibold uppercase">{title}</div>
          <div className="text-xs font-medium">{category}</div>

          <div>
            <span
              className={`rounded-md px-2 py-0.5 text-xs font-normal 
               ${
                 isInstalled
                   ? ' bg-black text-primary dark:bg-white dark:text-black '
                   : ' bg-grey-30 text-black'
               }`}
            >
              {t(isInstalled ? 'widget.add.alreadyInstalled' : membershipPlan)}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col w-full h-20">
        <div className="text-sm text-grey-50 font-normal overflow-hidden text-ellipsis px-3">
          {truncateString(description, 220)}
        </div>
      </div>

      <div className="flex w-full justify-end px-3">
        <Button variant="secondary" type="button" onClick={handlePreviewClick}>
          {t(`widget.add.preview`)}
        </Button>
        {!isAvailable || !isInstalled ? (
          <Button
            buttonClass="ml-2"
            variant="secondary"
            type="button"
            onClick={isAvailable ? handleInstallClick : handleUpgradeClick}
            loading={isInstallButtonLoading}
          >
            {t(`widget.add.${isAvailable ? 'install' : 'upgrade'}`)}
          </Button>
        ) : !isLocked ? (
          <Button
            buttonClass="ml-2"
            variant="secondary"
            type="button"
            onClick={handleRemoveClick()}
            loading={isRemoveButtonLoading}
          >
            {t(`widget.add.remove`)}
          </Button>
        ) : (
          <></>
        )}
      </div>
    </div>
  ) : (
    <div
      className={`flex flex-row justify-between min-w-80 h-auto bg-white border border-grey-10 rounded-4xl shadow-md py-3 dark:bg-grey-80 dark:border-grey-70
        ${className}
      `}
    >
      <div className="flex w-full px-3 items-center">
        <div
          className={
            'flex bg-lime min-w-14 w-14 h-14 rounded-xl overflow-hidden mr-4 '
          }
        >
          {iconUrl && <img src={iconUrl} alt={title} />}
        </div>
        <div className="flex flex-col justify-start">
          <div>
            <span className={'text-sm font-semibold uppercase mr-3'}>
              {title}
            </span>
            <span
              className={
                'text-xs text-grey-50 font-medium bg-grey-10 py-0.5 px-2 rounded-lg dark:bg-white dark:text-black'
              }
            >
              {membershipPlan}
            </span>
          </div>
          <div className="text-xs">{category}</div>
        </div>
      </div>

      <div className="flex w-auto justify-end items-center pr-6">
        {!isAvailable || !isInstalled ? (
          isInstallButtonLoading ? (
            <Loading status size="small" className="mr-3 h-4 w-5" />
          ) : (
            <>
              <Button
                variant="secondary"
                type="button"
                onClick={handlePreviewClick}
              >
                {t(`widget.add.preview`)}
              </Button>
              <Button
                buttonClass="ml-2"
                variant="tertiary"
                type="button"
                onClick={isAvailable ? handleInstallClick : handleUpgradeClick}
              >
                {t(`widget.add.${isAvailable ? 'install' : 'upgrade'}`)}
              </Button>
            </>
          )
        ) : (
          <Button
            variant="transparent"
            onClick={handleRemoveClick}
            loading={isRemoveButtonLoading}
            disabled={isLocked}
          >
            {t(`widget.add.remove`)}
          </Button>
        )}
      </div>
    </div>
  );
};

export default WidgetCard;
